import { Controller } from "@hotwired/stimulus"

// Match tiers. A hit on the item's own name always outranks one that needed the
// parent section, so searching "assist" puts "Assistant" above everything under
// "Delivery Assistance". Both constants exceed any achievable raw score, so the
// tier decides first and the raw score only orders within a tier.
const TIER_TITLE = 2000
const TIER_KEYWORD = 1000

// ⌘K / Ctrl-K command palette for jumping to any admin section.
//
// The destinations are server-rendered from NavigationCatalog (see
// app/models/navigation_catalog.rb and application/_command_palette), so this
// controller only handles open/close, fuzzy filtering, keyboard navigation, and
// highlighting. It stays dependency-free — the matcher is a small subsequence
// scorer, good enough for a fixed, ~90-item list.
//
// The trigger lives in the sidebar, outside this controller's element, so we
// open via a delegated window listener for [data-command-palette-open] plus the
// global ⌘K shortcut rather than a Stimulus action on the button.
export default class extends Controller {
  static targets = ["dialog", "input", "list", "item", "empty"]

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.onOpenClick = this.onOpenClick.bind(this)
    window.addEventListener("keydown", this.onKeydown)
    window.addEventListener("click", this.onOpenClick)
    this.activeIndex = -1
    this.defaultOrder()
    this.applyModifierHint()
  }

  // The sidebar trigger's shortcut hint lives outside this controller's element,
  // so resolve it globally: the ⌘ symbol on macOS, the word "Ctrl" everywhere
  // else — Windows/Linux users never see the Command key. Hidden in markup until
  // now so the wrong glyph never flashes before JS runs.
  applyModifierHint() {
    const platform = navigator.userAgentData?.platform || navigator.platform || ""
    const isMac = /mac|iphone|ipad|ipod/i.test(platform)
    document.querySelectorAll("[data-cp-hint]").forEach((hint) => {
      const mod = hint.querySelector("[data-cp-mod]")
      if (mod) {
        mod.textContent = isMac ? "⌘" : "Ctrl"
        mod.classList.toggle("is-symbol", isMac)
      }
      hint.hidden = false
    })
  }

  disconnect() {
    window.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("click", this.onOpenClick)
  }

  // ⌘K / Ctrl-K toggles the palette from anywhere.
  onKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault()
      this.toggle()
    }
  }

  // Any element marked [data-command-palette-open] opens it (the sidebar trigger).
  onOpenClick(event) {
    if (event.target.closest("[data-command-palette-open]")) {
      event.preventDefault()
      this.open()
    }
  }

  open() {
    if (this.dialogTarget.open) return
    this.dialogTarget.showModal()
    this.inputTarget.value = ""
    this.filter()
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  toggle() {
    if (this.dialogTarget.open) {
      this.close()
    } else {
      this.open()
    }
  }

  // Close when the backdrop (the dialog element itself, not the panel) is clicked.
  backdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  // Navigating to an item unloads the page; for external (_blank) links, tidy up.
  pick() {
    this.close()
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    const matched = []

    this.itemTargets.forEach((item) => {
      const score = query ? this.rank(query, item) : 0
      const hidden = query && score < 0
      item.parentElement.style.display = hidden ? "none" : ""
      if (!hidden) matched.push({ item, score })
    })

    // Best matches first while searching; restore the catalog order when the
    // query is cleared, so reopening the palette isn't left in search order.
    // Equal scores fall back to catalog position — filtering reorders the DOM,
    // so without an explicit index the tiebreak would drift as the user types.
    if (query) {
      matched.sort((a, b) => b.score - a.score || this.indexOf(a.item) - this.indexOf(b.item))
      matched.forEach(({ item }) => this.listTarget.appendChild(item.parentElement))
    } else {
      this.defaultOrder().forEach((row) => this.listTarget.appendChild(row))
    }

    this.emptyTarget.classList.toggle("hidden", matched.length > 0)
    this.setActive(0)
  }

  // The catalog order as first rendered, captured before any search reorders it.
  defaultOrder() {
    if (!this.rows) {
      this.rows = this.itemTargets.map((item) => item.parentElement)
      this.positions = new Map(this.itemTargets.map((item, index) => [item, index]))
    }
    return this.rows
  }

  // An item's position in the original catalog, for a stable ranking tiebreak.
  indexOf(item) {
    this.defaultOrder()
    return this.positions.get(item) ?? 0
  }

  // Tiered score for one item: its own name first, then its synonyms, and only
  // then the parent section — see TIER_TITLE / TIER_KEYWORD above. Returns -1
  // when the query matches nothing on the item.
  rank(query, item) {
    const { cpTitle = "", cpKeywords = "", cpSection = "" } = item.dataset

    const title = this.score(query, cpTitle)
    if (title >= 0) return TIER_TITLE + title

    const keywords = this.score(query, `${cpTitle} ${cpKeywords}`.trim())
    if (keywords >= 0) return TIER_KEYWORD + keywords

    return this.score(query, `${cpTitle} ${cpKeywords} ${cpSection}`.trim())
  }

  // Subsequence match with light scoring: reward consecutive hits and word
  // starts so "escset" ranks "Escalation Settings" above scattered matches.
  // Returns -1 when the query is not a subsequence of the haystack.
  score(query, haystack) {
    let qi = 0
    let score = 0
    let prev = -2
    for (let hi = 0; hi < haystack.length && qi < query.length; hi++) {
      if (haystack[hi] !== query[qi]) continue
      score += 1
      if (hi === prev + 1) score += 2
      if (hi === 0 || haystack[hi - 1] === " ") score += 3
      prev = hi
      qi++
    }
    return qi === query.length ? score : -1
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.setActive(this.activeIndex + 1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.setActive(this.activeIndex - 1)
        break
      case "Enter": {
        event.preventDefault()
        const active = this.visibleItems()[this.activeIndex]
        if (active) active.click()
        break
      }
      case "Escape":
        this.close()
        break
    }
  }

  visibleItems() {
    return this.itemTargets.filter((item) => item.parentElement.style.display !== "none")
  }

  setActive(index) {
    const visible = this.visibleItems()
    if (visible.length === 0) {
      this.activeIndex = -1
      return
    }
    this.activeIndex = (index + visible.length) % visible.length
    visible.forEach((item, i) => {
      const active = i === this.activeIndex
      item.classList.toggle("is-active", active)
      if (active) item.scrollIntoView({ block: "nearest" })
    })
  }
}
